// Texture asset upload: the image counterpart of glbUpload/audioUpload,
// through the same presign -> PUT -> confirm pipeline. Backs FxMaterial's
// albedo/normal/roughness/metalness map fields (docs/3d-content-pipeline/
// roadmap.md 3.2 Phase 1).
import type { StudioAssetRecord } from '../../engine';
import { apiPost, apiUpload } from '../services/api';
import { AssetValidationError } from './glbUpload';

export const MAX_TEXTURE_BYTES = 8 * 1024 * 1024;
export const MAX_TEXTURE_MIB = MAX_TEXTURE_BYTES / 1024 / 1024;

/** Upload content types by file extension; mirrors the API allowlist. */
const TEXTURE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

function contentTypeFor(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const contentType = TEXTURE_TYPES[ext];
  if (!contentType) {
    throw new AssetValidationError('Only .png, .jpg and .webp textures are supported.');
  }
  if (file.size > MAX_TEXTURE_BYTES) {
    throw new AssetValidationError(
      `Texture is ${(file.size / 1024 / 1024).toFixed(1)} MiB; the limit is ${MAX_TEXTURE_MIB} MiB.`,
    );
  }
  return contentType;
}

/**
 * Real content validation, not just a trust-the-extension check -
 * `contentTypeFor` above only looks at the filename, and its result is what
 * gets sent as the literal S3 PUT `Content-Type` header, so a file merely
 * renamed to a supported extension (or corrupt) would otherwise sail
 * through with an attacker-controlled content type. This decodes the file
 * as an image before it is ever uploaded, the same genuine decode-attempt
 * pattern `uploadGlbAsset`'s `GLTFLoader.parse` and `uploadAudioAsset`'s
 * `AudioContext.decodeAudioData` already use for their own asset kinds -
 * a file that fails to decode is rejected here, before any upload happens.
 */
async function validateImageContent(file: File): Promise<void> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new AssetValidationError('This file could not be read as an image - it may be corrupt, or not really an image.');
  }
  bitmap.close();
}

interface PresignWire {
  upload_url: string;
  asset_id: string;
  s3_key: string;
  cf_url: string;
}

interface ConfirmedAssetWire {
  id: string;
  name: string;
  cf_url: string;
  file_size: number;
  created_at: string;
}

/**
 * Validate a texture image, request a presigned URL, upload to S3, confirm
 * the asset, and return a StudioAssetRecord. The returned `downloadUrl` is
 * what FxMaterial's map fields store.
 */
export async function uploadTextureAsset(projectId: string, file: File): Promise<StudioAssetRecord> {
  const contentType = contentTypeFor(file);
  await validateImageContent(file);

  const presign = await apiPost<PresignWire>(`/projects/${projectId}/assets/presign`, {
    name: file.name,
    file_size: file.size,
    content_type: contentType,
  });

  await apiUpload(presign.upload_url, file, contentType);

  const confirmed = await apiPost<ConfirmedAssetWire>(`/projects/${projectId}/assets/confirm`, {
    asset_id: presign.asset_id,
    name: file.name.replace(/\.(png|jpe?g|webp)$/i, ''),
    file_size: file.size,
    content_type: contentType,
    s3_key: presign.s3_key,
    cf_url: presign.cf_url,
    meta: {},
  });

  return {
    id: confirmed.id,
    name: confirmed.name,
    kind: 'texture',
    storagePath: presign.s3_key,
    downloadUrl: confirmed.cf_url,
    bounds: { size: [1, 1, 1], center: [0, 0, 0] },
    category: 'structural',
    sizeBytes: confirmed.file_size,
    createdAt: new Date(confirmed.created_at).getTime(),
  };
}
